import { useRef, useState } from "react";
import { Selections } from "../../../services/api";
import TitledCover from "./TitledCover";

const PER_PAGE = 9; // 3 × 3

const startDate = (selection: Selections) =>
  `${new Date(selection.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${selection.year}`;

interface SelectionGridProps {
  selections: Selections[];
  onOpen: (selection: Selections) => void;
}

// Available selections as swipeable pages of 3 × 3 covers (title on the cover, date below),
// like the mobile app. Swipe / scroll sideways, or use the arrows and dots.
const SelectionGrid = ({ selections, onOpen }: SelectionGridProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const pages: Selections[][] = [];
  for (let i = 0; i < selections.length; i += PER_PAGE) pages.push(selections.slice(i, i + PER_PAGE));

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (track) setPage(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <div className="selectionGrid">
      <div className="selectionGrid__track" ref={trackRef} onScroll={onScroll}>
        {pages.map((items) => (
          <div className="selectionGrid__page" key={items[0].id}>
            {items.map((selection) => (
              <button key={selection.id} type="button" className="selectionGrid__tile" onClick={() => onOpen(selection)}>
                <TitledCover selection={selection} size={300} />
                <span className="selectionGrid__date">{startDate(selection)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {pages.length > 1 && (
        <div className="selectionGrid__nav">
          <button
            type="button"
            className="selectionGrid__arrow"
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <div className="selectionGrid__dots">
            {pages.map((items, index) => (
              <button
                key={items[0].id}
                type="button"
                className={`selectionGrid__dot${index === page ? " selectionGrid__dot--active" : ""}`}
                onClick={() => goTo(index)}
                aria-label={`Page ${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="selectionGrid__arrow"
            onClick={() => goTo(page + 1)}
            disabled={page === pages.length - 1}
            aria-label="Next page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SelectionGrid;
